import React, { useState, useMemo } from "react";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { Trash2, Archive, ShieldAlert, ScanSearch, Lock, TriangleAlert } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };
const key = (coll, id) => `${coll}::${id}`;

export default function DemoDataCleanup() {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState("archive");
  const [confirmText, setConfirmText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/security/demo-cleanup/scan");
      setScan(data);
      // default-select strong matches (not needs_review)
      const s = new Set();
      (data.groups || []).forEach((g) => g.samples.forEach((r) => { if (!r.needs_review) s.add(key(g.collection, r.id)); }));
      setSelected(s);
      setConfirmText("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setScanning(false); }
  };

  const toggle = (coll, id) => setSelected((prev) => {
    const n = new Set(prev); const k = key(coll, id);
    n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  const setGroup = (g, on) => setSelected((prev) => {
    const n = new Set(prev);
    g.samples.forEach((r) => { const k = key(g.collection, r.id); on ? n.add(k) : n.delete(k); });
    return n;
  });

  const selectedItems = useMemo(() => Array.from(selected).map((k) => {
    const [collection, id] = k.split("::"); return { collection, id };
  }), [selected]);

  const canExecute = selectedItems.length > 0 && (mode === "archive" || confirmText.trim() === "DELETE TEST DATA");

  const execute = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post("/security/demo-cleanup/execute", { mode, items: selectedItems, confirm_text: confirmText });
      toast.success(data.message || "Done");
      setConfirmOpen(false);
      await runScan();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setExecuting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto pb-20" data-testid="demo-cleanup-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <ScanSearch size={22} style={{ color: C.navy }} /> Demo / Test Data Cleanup
      </h1>
      <p className="text-[13px] mb-4" style={{ color: C.slate }}>
        Super-Admin-only utility to safely find and remove seeded demo/test/QA records before launch.
      </p>

      {/* Safety banner */}
      <Card className="p-4 mb-5 border-[#EADFBF] bg-[#FBF7EC]" data-testid="demo-cleanup-warning">
        <div className="flex items-start gap-2.5">
          <TriangleAlert size={18} style={{ color: C.amber }} className="mt-0.5 shrink-0" />
          <div className="text-[12.5px] leading-relaxed" style={{ color: "#8a6d1f" }}>
            <div className="font-semibold mb-0.5" style={{ color: C.amber }}>This tool is for demo/test data only.</div>
            Review records carefully before deleting. Use <b>Archive</b> when unsure — it is reversible.
            <b> Real family/client records should never be deleted through this tool.</b> Every action is audited.
          </div>
        </div>
      </Card>

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Button onClick={runScan} disabled={scanning} className="gap-1.5 bg-[#1C4358]" data-testid="demo-cleanup-scan-btn">
          <ScanSearch size={15} /> {scan ? "Re-run dry-run scan" : "Run dry-run scan"}
        </Button>
        <span className="text-[12px]" style={{ color: C.slate }}>Dry run only changes nothing until you confirm below.</span>
      </div>

      {scanning && <Skeleton className="h-52" data-testid="demo-cleanup-loading" />}

      {scan && !scanning && (
        <div data-testid="demo-cleanup-results">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[13px]" style={{ color: C.charcoal }}>
              <b style={{ color: C.navy }}>{scan.total_matched}</b> suspected test/demo record(s) across {scan.groups.length} collection(s).
              <span className="ml-2" style={{ color: C.slate }}>{selectedItems.length} selected</span>
            </div>
          </div>

          {scan.groups.length === 0 && (
            <Card className="p-6 text-center text-[13px]" style={{ color: C.green }} data-testid="demo-cleanup-empty">
              No demo/test records found. Your data looks clean. 🎉
            </Card>
          )}

          {scan.groups.map((g) => {
            const allOn = g.samples.every((r) => selected.has(key(g.collection, r.id)));
            return (
              <Card key={g.collection} className="p-3.5 mb-3 border-[#E6EDF2]" data-testid={`demo-group-${g.collection}`}>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="text-[13px] font-semibold flex items-center gap-2" style={{ color: C.charcoal }}>
                    {g.label}
                    <span className="text-[11px] font-normal px-1.5 py-0.5 rounded" style={{ background: "#F1F5F9", color: C.slate }}>{g.count}</span>
                    {g.archive_only && <span className="text-[10.5px] px-1.5 py-0.5 rounded flex items-center gap-1" style={{ background: "#EEF6FF", color: C.navy }}><Lock size={10} /> archive-only</span>}
                    {g.needs_review_count > 0 && <span className="text-[10.5px] px-1.5 py-0.5 rounded" style={{ background: "#FBF7EC", color: C.amber }}>{g.needs_review_count} need review</span>}
                  </div>
                  <button className="text-[11.5px] underline" style={{ color: C.navy }} onClick={() => setGroup(g, !allOn)} data-testid={`demo-group-toggle-${g.collection}`}>
                    {allOn ? "Deselect all" : "Select all shown"}
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <tbody>
                      {g.samples.map((r) => (
                        <tr key={r.id} className="border-t" style={{ borderColor: C.line }} data-testid={`demo-row-${r.id}`}>
                          <td className="py-1.5 pr-2 w-6 align-top">
                            <Checkbox checked={selected.has(key(g.collection, r.id))} onCheckedChange={() => toggle(g.collection, r.id)} data-testid={`demo-check-${r.id}`} />
                          </td>
                          <td className="py-1.5 pr-3 align-top font-medium" style={{ color: C.charcoal }}>{r.name}</td>
                          <td className="py-1.5 pr-3 align-top" style={{ color: C.slate }}>{r.reason}</td>
                          <td className="py-1.5 pr-3 align-top whitespace-nowrap" style={{ color: C.slate }}>{r.created_at ? formatDateTime(r.created_at) : "—"}</td>
                          <td className="py-1.5 align-top">
                            {r.needs_review
                              ? <span className="text-[10.5px] px-1.5 py-0.5 rounded" style={{ background: "#FBF7EC", color: C.amber }}>needs review</span>
                              : <span className="text-[10.5px] px-1.5 py-0.5 rounded" style={{ background: "#ECFBF3", color: C.green }}>test</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {g.sample_truncated && <div className="text-[11px] mt-1.5" style={{ color: C.slate }}>Showing the first {g.samples.length} of {g.count}.</div>}
              </Card>
            );
          })}

          {/* Protected note */}
          <Card className="p-3.5 mb-4 border-[#E6EDF2]" data-testid="demo-cleanup-protected">
            <div className="text-[12px] font-semibold flex items-center gap-1.5 mb-1" style={{ color: C.navy }}><Lock size={13} /> Never touched by this tool</div>
            <div className="text-[11.5px]" style={{ color: C.slate }}>{(scan.protected || []).join(" · ")}</div>
          </Card>

          {/* Action bar */}
          {scan.total_matched > 0 && (
            <Card className="p-4 sticky bottom-4 border-[#E6EDF2] shadow-lg bg-white" data-testid="demo-cleanup-actions">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-3 text-[12.5px]">
                  <label className="flex items-center gap-1.5 cursor-pointer" style={{ color: mode === "archive" ? C.navy : C.slate }}>
                    <input type="radio" name="mode" checked={mode === "archive"} onChange={() => setMode("archive")} data-testid="demo-mode-archive" />
                    <Archive size={14} /> Archive selected
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer" style={{ color: mode === "delete" ? C.red : C.slate }}>
                    <input type="radio" name="mode" checked={mode === "delete"} onChange={() => setMode("delete")} data-testid="demo-mode-delete" />
                    <Trash2 size={14} /> Delete selected
                  </label>
                </div>
                {mode === "delete" && (
                  <div className="flex items-center gap-2">
                    <span className="text-[11.5px]" style={{ color: C.red }}>Type <b>DELETE TEST DATA</b> to confirm</span>
                    <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE TEST DATA"
                      className="h-9 w-44 text-[12px]" data-testid="demo-confirm-input" aria-label="Delete confirmation phrase" />
                  </div>
                )}
                <div className="ml-auto">
                  <Button onClick={() => setConfirmOpen(true)} disabled={!canExecute}
                    className={mode === "delete" ? "bg-[#9C1E22] gap-1.5" : "bg-[#1C4358] gap-1.5"} data-testid="demo-execute-btn">
                    {mode === "delete" ? <Trash2 size={15} /> : <Archive size={15} />}
                    {mode === "delete" ? `Delete ${selectedItems.length}` : `Archive ${selectedItems.length}`}
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="demo-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: mode === "delete" ? C.red : C.navy }}>
              <ShieldAlert size={18} /> {mode === "delete" ? "Confirm permanent delete" : "Confirm archive"}
            </DialogTitle>
            <DialogDescription>
              {mode === "delete"
                ? `This will permanently delete ${selectedItems.length} selected record(s). Files/documents are archived, not deleted. This cannot be undone.`
                : `This will archive (soft-delete) ${selectedItems.length} selected record(s). They can be restored by an administrator.`}
              {" "}Records are re-verified as test/demo before any action — anything that no longer matches is skipped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="demo-confirm-cancel">Cancel</Button>
            <Button onClick={execute} disabled={executing || !canExecute}
              className={mode === "delete" ? "bg-[#9C1E22]" : "bg-[#1C4358]"} data-testid="demo-confirm-go">
              {executing ? "Working…" : (mode === "delete" ? "Delete now" : "Archive now")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
